import { MouseEvent, useCallback } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Skeleton } from '@mui/material';
import {
  BaseTableProps,
  DEFAULT_PAGE_SIZE,
  FindAllFilterParams,
  getRowsPerPageDefault,
  ObjectWithId,
  Order,
  SearchFilter,
  SorterParams,
  TableCol,
} from '../../../../lib/util/table-util';
import Empty from '../../custom-icons/Empty';
import CustomTableHead from '../shared/CustomTableHead';
import { getArrayRange } from '../../../../lib/util/misc-utils';
import _ from 'lodash';
import DraggableBodyRow from './DraggableBodyRow';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import classes from './DraggableBodyRow.module.css';
import { DtoStringKeysAsPath } from '../../../../lib/util/type-utils';

interface DraggableTableProps<ViewDto extends ObjectWithId>
  extends BaseTableProps<ViewDto> {
  columns: TableCol<ViewDto>[];
  onRowMove?: (input: any[]) => any;
}

export type RowMoveNumericIdInputType = { id: number; sortOrder: number }[];
export type RowMoveStringIdInputType = { id: string; sortOrder: number }[];

export default function DraggableTable<ViewDto extends ObjectWithId>({
  columns,
  apiRes,
  isFetching,
  setTableState,
  tableState,
  onRowClick,
  onRefresh,
  onDownload,
  onRowMove,
}: DraggableTableProps<ViewDto>) {
  // Avoid a layout jump when reaching the last page with empty rows.
  let emptyRows = getRowsPerPageDefault();

  if (apiRes?.data && !isFetching)
    emptyRows = apiRes.pagination.pageSize - apiRes.data.length;

  const handleRequestSort = (property: DtoStringKeysAsPath<ViewDto> | 'id') => {
    //get existing sort order
    const { order, orderBy } = getSortOrder();
    const currentlyIsAsc = orderBy === property && order === 'asc';

    //build new sorter
    const sorter = {
      [property]: currentlyIsAsc ? 'desc' : 'asc',
    } as SorterParams<ViewDto>;

    setTableState({
      pagination: tableState.pagination
        ? { ...tableState.pagination, current: 1 }
        : { current: 1, pageSize: DEFAULT_PAGE_SIZE },
      filters: tableState.filters ? { ...tableState.filters } : null,
      sorter: sorter,
    });
  };

  const handleSearch = (filter: SearchFilter<ViewDto>) => {
    if (filter.value === '' && tableState.filters) {
      const filtersCopy = { ...tableState.filters };
      delete filtersCopy[filter.field];
      setTableState({
        pagination: tableState.pagination
          ? { ...tableState.pagination, current: 1 }
          : { current: 1, pageSize: getRowsPerPageDefault() },
        filters: filtersCopy,
        sorter: null,
      });
    } else {
      const filterField = {
        [filter.field]: { [filter.type]: filter.value },
      } as FindAllFilterParams<ViewDto>;
      setTableState({
        pagination: tableState.pagination
          ? { ...tableState.pagination, current: 1 }
          : { current: 1, pageSize: getRowsPerPageDefault() },
        filters: tableState.filters
          ? {
              ...tableState.filters,
              ...filterField,
            }
          : filterField,
        sorter: null,
      });
    }
  };

  const getSortOrder = (): {
    order: Order;
    orderBy: DtoStringKeysAsPath<ViewDto> | 'id';
  } => {
    if (!tableState.sorter) {
      //no sorter, if default specified on columns use it.
      //otherwise use id asc.
      const defaultSortColumn = columns.find((col) => col.defaultSort);

      return {
        order: defaultSortColumn?.defaultSort || 'asc',
        orderBy: defaultSortColumn?.id || 'id',
      };
    } else {
      //{name:'asc'}
      const keys = Object.keys(tableState.sorter);
      if (!keys) return { order: 'asc', orderBy: 'id' };

      const orderBy = keys[0] as DtoStringKeysAsPath<ViewDto> | 'id';
      const order = tableState.sorter[orderBy] as Order;
      return {
        order,
        orderBy,
      };
    }
  };

  const { order, orderBy } = getSortOrder();

  //Methods Related to Dragging Row
  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (!apiRes) return;

      const data = apiRes.data.map((row) => ({
        id: row.id,
      }));

      const rowMoving = data.splice(dragIndex, 1)[0];

      data.splice(hoverIndex, 0, rowMoving);

      const sortedData = data.map((row, index) => ({
        id: row.id,
        sortOrder: index,
      }));

      onRowMove?.(sortedData);
    },
    [apiRes, onRowMove],
  );

  return (
    <Paper sx={{ maxWidth: '95%', overflowX: 'auto' }}>
      <Table aria-labelledby="Table" size={'small'}>
        <CustomTableHead<ViewDto>
          columns={columns}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          onSearch={handleSearch}
          onRefresh={onRefresh}
          onDownload={onDownload}
          filters={tableState.filters}
          sorter={tableState.sorter}
        />
        <TableBody>
          <DndProvider backend={HTML5Backend}>
            {isFetching && (
              <>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} align="right"></TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    {getArrayRange(1, emptyRows, 1).map((num) => (
                      <Skeleton height={44} key={num} />
                    ))}
                  </TableCell>
                </TableRow>
              </>
            )}
            {apiRes?.data && apiRes.data.length > 0 && !isFetching && (
              <>
                {apiRes.data.map((data, index) => (
                  <DraggableBodyRow
                    hover
                    className={classes['draggable-row']}
                    index={index}
                    moveRow={moveRow}
                    onClick={
                      onRowClick
                        ? (e: MouseEvent) => onRowClick(data, e)
                        : (): any => null
                    }
                    key={data.id}
                  >
                    {columns.map((col, index) =>
                      index === 0 ? (
                        <TableCell key={col.id} align={col.align || 'left'}>
                          {col.render ? (
                            <>
                              <DragIndicatorIcon style={{ float: 'left' }} />
                              {col.render(data)}
                            </>
                          ) : (
                            _.get(data, col.id)
                          )}
                        </TableCell>
                      ) : (
                        <TableCell key={col.id} align={col.align || 'left'}>
                          {col.render ? col.render(data) : _.get(data, col.id)}
                        </TableCell>
                      ),
                    )}
                  </DraggableBodyRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 44 * emptyRows,
                    }}
                  >
                    <TableCell colSpan={columns.length} />
                  </TableRow>
                )}
              </>
            )}
            {apiRes?.data.length === 0 && !isFetching && (
              <>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} align="right"></TableCell>
                  ))}
                </TableRow>
                <TableRow
                  style={{
                    height: 33 * emptyRows,
                  }}
                >
                  <TableCell
                    colSpan={columns.length}
                    sx={{ textAlign: 'center' }}
                  >
                    <Empty />
                  </TableCell>
                </TableRow>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} align="right"></TableCell>
                  ))}
                </TableRow>
              </>
            )}
          </DndProvider>
        </TableBody>
      </Table>
    </Paper>
  );
}
